import { useCallback, useEffect, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../store';
import { useStakeAdjustmentStore } from '../store/states/stakeAdjustment';
import { CUSTOM_FILTER_INSTANCE_ID_SUFFIX } from '../utils/constants';
import { getTableFilterInstanceId } from '../utils/filterInstanceId';

type FullscreenInstanceIds = {
  fullscreenInstanceStateId: string;
  widgetFullscreenInstanceStateId: string;
  customPageFilterStoreId: string;
  cloneFullscreenInstanceState: () => void;
  deleteFullscreenInstanceState: () => void;
};

export type UseFullscreenInstanceIdsParams = {
  pageFilterInstanceId: string;
  widgetFilterInstanceId: string;
};

export function useFullscreenInstanceIds({
  pageFilterInstanceId,
  widgetFilterInstanceId,
}: UseFullscreenInstanceIdsParams): FullscreenInstanceIds {
  const customPageFilterStoreId = useMemo(
    () => pageFilterInstanceId,
    [pageFilterInstanceId]
  );
  const fullscreenInstanceStateId = useMemo(() => uuid(), []);
  const widgetFullscreenInstanceStateId = useMemo(() => uuid(), []);

  const {
    cloneFilterSelections,
    deleteFilterSelections,
    cloneWorkflowStateSelection,
    deleteWorkflowStateSelection,
    cloneTableGroupExpansions,
    deleteTableGroupExpansions,
    cloneSortColumns,
    deleteSortColumns,
  } = useStore(
    useShallow((state) => ({
      cloneFilterSelections: state.cloneFilterSelections,
      deleteFilterSelections: state.deleteFilterSelections,
      cloneWorkflowStateSelection: state.cloneWorkflowStateSelection,
      deleteWorkflowStateSelection: state.deleteWorkflowStateSelection,
      cloneTableGroupExpansions: state.cloneTableGroupExpansions,
      deleteTableGroupExpansions: state.deleteTableGroupExpansions,
      cloneSortColumns: state.cloneSortColumns,
      deleteSortColumns: state.deleteSortColumns,
    }))
  );

  const { cloneStakeAdjusted, deleteStakeAdjusted } = useStakeAdjustmentStore(
    useShallow((state) => ({
      cloneStakeAdjusted: state.cloneStakeAdjusted,
      deleteStakeAdjusted: state.deleteStakeAdjusted,
    }))
  );

  const cloneFullscreenInstanceState = useCallback(() => {
    const tableFilterInstanceId = getTableFilterInstanceId(
      widgetFilterInstanceId,
      pageFilterInstanceId
    );
    const fullscreenTableFilterInstanceId = getTableFilterInstanceId(
      widgetFullscreenInstanceStateId,
      fullscreenInstanceStateId
    );

    cloneFilterSelections(pageFilterInstanceId, fullscreenInstanceStateId);
    cloneFilterSelections(
      `${pageFilterInstanceId}${CUSTOM_FILTER_INSTANCE_ID_SUFFIX}`,
      `${fullscreenInstanceStateId}${CUSTOM_FILTER_INSTANCE_ID_SUFFIX}`
    );
    cloneFilterSelections(
      tableFilterInstanceId,
      fullscreenTableFilterInstanceId
    );
    cloneWorkflowStateSelection(
      pageFilterInstanceId,
      widgetFilterInstanceId,
      fullscreenInstanceStateId,
      widgetFullscreenInstanceStateId
    );
    cloneTableGroupExpansions(
      pageFilterInstanceId,
      widgetFilterInstanceId,
      fullscreenInstanceStateId,
      widgetFullscreenInstanceStateId
    );
    cloneSortColumns(
      pageFilterInstanceId,
      widgetFilterInstanceId,
      fullscreenInstanceStateId,
      widgetFullscreenInstanceStateId
    );
    cloneStakeAdjusted(pageFilterInstanceId, fullscreenInstanceStateId);
  }, [
    cloneFilterSelections,
    cloneSortColumns,
    cloneStakeAdjusted,
    cloneTableGroupExpansions,
    cloneWorkflowStateSelection,
    fullscreenInstanceStateId,
    pageFilterInstanceId,
    widgetFilterInstanceId,
    widgetFullscreenInstanceStateId,
  ]);

  const deleteFullscreenInstanceState = useCallback(() => {
    const fullscreenTableFilterInstanceId = getTableFilterInstanceId(
      widgetFullscreenInstanceStateId,
      fullscreenInstanceStateId
    );

    deleteFilterSelections(fullscreenInstanceStateId);
    deleteFilterSelections(fullscreenTableFilterInstanceId);
    deleteWorkflowStateSelection(
      fullscreenInstanceStateId,
      widgetFullscreenInstanceStateId
    );
    deleteTableGroupExpansions(
      fullscreenInstanceStateId,
      widgetFullscreenInstanceStateId
    );
    deleteSortColumns(
      fullscreenInstanceStateId,
      widgetFullscreenInstanceStateId
    );
    deleteStakeAdjusted(fullscreenInstanceStateId);
  }, [
    deleteFilterSelections,
    deleteSortColumns,
    deleteStakeAdjusted,
    deleteTableGroupExpansions,
    deleteWorkflowStateSelection,
    fullscreenInstanceStateId,
    widgetFullscreenInstanceStateId,
  ]);

  useEffect(() => {
    return deleteFullscreenInstanceState;
  }, [deleteFullscreenInstanceState]);

  return {
    customPageFilterStoreId,
    fullscreenInstanceStateId,
    widgetFullscreenInstanceStateId,
    cloneFullscreenInstanceState,
    deleteFullscreenInstanceState,
  };
}
